import { useEffect, useCallback, useState, RefObject } from 'react';
import { useFocusTrap, FOCUSABLE_ELEMENTS_SELECTOR } from '../hooks';

interface UseDialogFocusProps {
    active?: boolean;
    loading?: boolean;
    dialogRef: RefObject<HTMLDivElement>;
    anchorElement?: HTMLElement | null;
}

export const useDialogFocus = ({ active, loading, dialogRef, anchorElement }: UseDialogFocusProps) => {
    const [dialogElement, setDialogElement] = useState<HTMLDivElement | null>(null);

    const focusElement = useCallback((): void => {
        if (!dialogRef.current) return;

        const firstFocusable = dialogRef.current.querySelector<HTMLElement>(FOCUSABLE_ELEMENTS_SELECTOR);
        if (firstFocusable) {
            firstFocusable.focus();
        } else {
            // Fallback: focus dialog itself
            dialogRef.current.setAttribute('tabindex', '-1');
            dialogRef.current.focus();
        }
    }, [dialogRef]);

    // Handle initial focus when dialog opens
    useEffect(() => {
        if (active && !loading) {
            const focusRequest = window.requestAnimationFrame(() => {
                focusElement();
            });

            return () => window.cancelAnimationFrame(focusRequest);
        }
    }, [active, loading, focusElement]);

    // Track when dialog element becomes available for focus trapping
    useEffect(() => {
        if (active && dialogRef.current && dialogRef.current !== dialogElement) {
            setDialogElement(dialogRef.current);
        } else if (!active) {
            setDialogElement(null);
        }
    }, [active, dialogRef, dialogElement]);

    // Handle focus trapping within the dialog only when element is available
    useFocusTrap(active && dialogElement ? dialogElement : null);

    // Handle focus return to anchor element when dialog closes
    useEffect(() => {
        if (!active && anchorElement) {
            const focusRequest = window.requestAnimationFrame(() => {
                anchorElement.focus();
            });

            return () => window.cancelAnimationFrame(focusRequest);
        }
    }, [active, anchorElement]);
};
